// External modules
import type { NormalizedCacheObject } from 'apollo-cache-inmemory';
import type {
  ApolloQueryResult,
  MutationOptions,
  OperationVariables,
  QueryOptions,
} from 'apollo-client';
import type ApolloClient from 'apollo-client';
import type { FetchResult } from 'apollo-link';
import type { ErrorResponse } from 'apollo-link-error';
import type { GraphQLError } from 'graphql';

// Internal modules
import {
  ACCOUNT_CREATION_OPERATIONS,
  OPERATION_TO_ACCESS_TOKEN_MAP,
} from './constants';
import { logger } from '@/lib/logger';
import type { GenericObjectType } from '@/types';

const NO_ERROR_MESSAGE_AVAILABLE = 'NO_ERROR_MESSAGE_AVAILABLE';

type HandleGraphQLErrors = {
  apiCorrelationId: string;
  graphQLErrors?: ReadonlyArray<GraphQLError>;
  operationName: string;
};

type HandleNetworkError = {
  apiCorrelationId: string;
  networkError?: Error;
  operationName: string;
};

export const getApiCorrelationId = ({
  networkError,
  response,
}: {
  // TODO refactor into standard error types from apollo DTXOGROWTH-619
  networkError?: any;
  response?: any;
}): string => {
  const { extensions = {} } = networkError?.result || response || {};

  /**
   * In xobuyer and graphqlnode web we set the corrId on the
   * extensions object as `correlation_id` but graphqlnodeweb
   * also uses a plugin that sets it as `correlationId`.
   * So sometimes one is present and sometimes the other is...
   */
  const { correlationId, correlation_id } = extensions;

  return correlation_id || correlationId || 'NOT_AVAILABLE';
};

export const isApplePay = (opName: string): boolean => {
  return opName === ACCOUNT_CREATION_OPERATIONS.APPROVE_GUEST_PAYMENT_APPLE_PAY;
};

export const isOnboardGuest = (opName: string): boolean => {
  return opName === ACCOUNT_CREATION_OPERATIONS.ONBOARD_GUEST;
};

export const isSignUpNewMember = (opName: string): boolean => {
  return opName === ACCOUNT_CREATION_OPERATIONS.SIGN_UP_NEW_MEMBER;
};

export const isCreateMemberAccount = (opName: string): boolean => {
  return opName === ACCOUNT_CREATION_OPERATIONS.CREATE_MEMBER_ACCOUNT;
};

export const isAccountCreationMutation = (opName: string): boolean => {
  return (
    isApplePay(opName) ||
    isOnboardGuest(opName) ||
    isSignUpNewMember(opName) ||
    isCreateMemberAccount(opName)
  );
};

/**
 * @function getAccessTokenType
 * @param {string} opName
 * @returns {string}
 * @description Takes an operation name
 */
export const getAccessTokenType = (opName: string): string => {
  // @ts-expect-error we dont require the opName to exist in the map
  return OPERATION_TO_ACCESS_TOKEN_MAP[opName] || null;
};

/**
 * @function mapMutationNameToDataNamespace
 * @param {string} opName
 * @returns {string}
 * @description Takes the name of a mutation, e.g. `SignUpNewMemberMutation`,
 * and maps it to a the namespace that the data queried for is under,
 * e.g. `signUpNewMember`.
 */
export const mapMutationNameToDataNamespace = (name: string): string => {
  return name
    .replace(/Mutation$/, '')
    .replace(/^(\w)/, (_, first) => first?.toLowerCase());
};

export const getErrorFromResponse = (response: ErrorResponse): any => {
  const { graphQLErrors, networkError } = response;
  const firstGQLError = graphQLErrors?.[0];
  // @ts-expect-error result doesn't exist on Error
  const firstNetworkError = networkError?.result?.errors?.[0];

  return firstGQLError || firstNetworkError || response;
};

export const requestFactoryQuery = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  return <T = any, TVariables = OperationVariables>(
    options: QueryOptions<TVariables, T>,
  ): Promise<ApolloQueryResult<T>> => {
    return client.query<T, TVariables>(options).catch((response: any) => {
      const error = getErrorFromResponse(response);
      throw error;
    });
  };
};

export const requestFactoryMutate = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  return <T = any, TVariables = OperationVariables>(
    options: MutationOptions<T, TVariables>,
  ): Promise<FetchResult<T>> => {
    return client.mutate<T, TVariables>(options).catch((response: any) => {
      const error = getErrorFromResponse(response);
      throw error;
    });
  };
};

export const handleGraphQLErrors = ({
  apiCorrelationId,
  graphQLErrors,
  operationName,
}: HandleGraphQLErrors): void => {
  graphQLErrors
    // `xobuyernodeserv` is throwing a client contingency when there are no matches
    // for the `AddressAutocompleteQuery`. `GAAC_error_ZERO_RESULTS` should neither
    // be an error nor logged; it's polluting our error charts.
    ?.filter(error => error?.message !== 'GAAC_error_ZERO_RESULTS')
    ?.forEach?.(error => {
      const { extensions = {}, message = NO_ERROR_MESSAGE_AVAILABLE } = error;
      const { exception = {} } = extensions;
      let { code = message } = extensions;
      let errDetails = '';

      // if OAS_ERROR, add the err details to the err code we log to FPTI
      if (code === 'OAS_ERROR') {
        // @ts-expect-error errorData does not exist on GraphQLError
        errDetails = error?.errorData
          ?.map((errDetailObj: GenericObjectType) => {
            return errDetailObj?.code;
          })
          ?.join('|');

        // OAS_ERROR -> OAS_ERROR_INVALID_FIRST_NAME-INVALID_LAST_NAME etc...
        // This will also allow us to log in SignalFx by OAS error type
        code = code + '_' + errDetails;
      }

      // Logging once per error instead of using a shared payload
      logger.error(`graphql_error_${operationName}_${code}`, {
        apiCorrelationId,
        message,
        stack: exception?.stacktrace?.join(' \\n ') || '',
      });
      // logs directly to SignalFX and bypasses cal
      logger.metric('GUEST_SIGNUP.GRAPHQL.ERROR', {
        dimensions: {
          code,
          operation_name: operationName,
        },
      });

      logger.fpti('FPTI_API_ERROR', {
        error_source: operationName,
        int_error_code: code,
        int_error_desc: 'gqlContingency',
        int_error_details: errDetails,
        state_name: 'billing',
        transition_name: 'process_api_error',
      });
    });
};

export const handleNetworkError = ({
  apiCorrelationId,
  operationName,
  networkError,
}: HandleNetworkError): void => {
  if (!networkError) {
    return;
  }
  // Log used to populate our monitoring dashboards for status codes per
  // operation. `networkError` will not be available for errors resulting
  // from invalid user input or errors like `ACCOUNT_ALREADY_EXISTS`.
  // @ts-expect-error statusCode only exists on NetworkError and not just Error
  const networkErrorStatusCode = networkError?.statusCode ?? 'NOT_AVAILABLE';
  // logs directly to SignalFx and bypasses cal
  logger.metric('GUEST_SIGNUP.NETWORK.ERROR', {
    dimensions: {
      operation_name: operationName,
      status_code: networkErrorStatusCode,
    },
  });
  logger.error('network_error', {
    apiCorrelationId,
    operationName,
    statusCode: networkErrorStatusCode,
  });
};
